import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Sparkles, FolderTree, Plus, X, Building2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ImageUpload } from './ImageUpload';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { uploadCustomizationLogo, deleteCustomizationLogo } from '@/services/customization.service';

interface CustomizationModalProps {
  open: boolean;
  onClose: () => void;
  currentImage: string | null;
  defaultTopics: string[] | null;
  defaultCompany: string | null;
  onSave: (imageUrl: string | null, defaultTopics: string[], defaultCompany: string | null) => Promise<void>;
}

export function CustomizationModal({
  open,
  onClose,
  currentImage,
  defaultTopics,
  defaultCompany,
  onSave,
}: CustomizationModalProps) {
  const { t } = useTranslation('common');
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(currentImage);
  // previewUrl = data URL local (FileReader) ou signed URL do banco — nunca URL pública
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImage);
  const [tempTopics, setTempTopics] = useState<string[]>(defaultTopics || []);
  const [tempCompany, setTempCompany] = useState<string>(defaultCompany || '');
  const [newTopicName, setNewTopicName] = useState('');
  const [saving, setSaving] = useState(false);

  // Sincroniza estado quando o modal abre (re-abertura com novos dados do pai)
  useEffect(() => {
    if (open) {
      setTempImageUrl(currentImage);
      setPreviewUrl(currentImage);
      setTempTopics(defaultTopics || []);
      setTempCompany(defaultCompany || '');
      setNewTopicName('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddTopic = () => {
    const trimmed = newTopicName.trim();
    if (trimmed && !tempTopics.includes(trimmed)) {
      setTempTopics([...tempTopics, trimmed]);
      setNewTopicName('');
    }
  };

  const handleRemoveTopic = (index: number) => {
    setTempTopics(tempTopics.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave(tempImageUrl, tempTopics, tempCompany.trim() || null);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar personalização:', error);
      alert('Erro ao salvar personalização. Verifique o console para mais detalhes.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setTempImageUrl(currentImage);
    setPreviewUrl(currentImage);
    setTempTopics(defaultTopics || []);
    setTempCompany(defaultCompany || '');
    setNewTopicName('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader className="space-y-1.5 flex-shrink-0">
          <DialogTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {t('customization.title')}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {t('customization.description', 'Personalize a aparência das suas atas de reunião')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2 overflow-y-auto pr-2 flex-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
          {}
          <Card className="shadow-none border">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm flex items-center gap-2">
                <FolderTree className="h-4 w-4 text-primary" />
                {t('customization.default_topics')}
              </CardTitle>
              <CardDescription className="text-xs">
                {t('customization.default_topics_description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 px-4 pb-4 pt-0">
              {}
              <div className="flex gap-2">
                <Input
                  id="new-topic"
                  placeholder={t('customization.topic_placeholder', 'Ex: Abertura, Discussão...')}
                  value={newTopicName}
                  onChange={(e) => setNewTopicName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTopic();
                    }
                  }}
                  disabled={saving}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={handleAddTopic}
                  disabled={!newTopicName.trim() || saving}
                  size="icon"
                  className="h-9 w-9"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {}
              {tempTopics.length > 0 ? (
                <div className="space-y-1.5">
                  {tempTopics.map((topic, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-muted/50 rounded-md p-2 group hover:bg-muted transition-colors"
                    >
                      <span className="text-xs font-medium">{topic}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleRemoveTopic(index)}
                        disabled={saving}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 border border-dashed border-border rounded-md">
                  <FolderTree className="h-6 w-6 mx-auto text-muted-foreground/50 mb-1.5" />
                  <p className="text-xs text-muted-foreground">
                    {t('customization.no_topics', 'Nenhum tópico padrão configurado')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Empresa Padrão */}
          <Card className="shadow-none border">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4 text-primary" />
                {t('customization.default_company')}
              </CardTitle>
              <CardDescription className="text-xs">
                {t('customization.default_company_description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              <Input
                id="default-company"
                placeholder={t('customization.company_placeholder', 'Ex: Minha Empresa Ltda')}
                value={tempCompany}
                onChange={(e) => setTempCompany(e.target.value)}
                disabled={saving}
              />
            </CardContent>
          </Card>

          {}
          <Card className="shadow-none border">
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-sm flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-primary" />
                {t('customization.header_image')}
              </CardTitle>
              <CardDescription className="text-xs">
                {t('customization.header_image_description', 'Logo exibida no topo das atas. Recomendado: 1200x300px')}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              <ImageUpload
                currentImage={tempImageUrl}
                onImageChange={setTempImageUrl}
                onPreviewChange={(dataUrl) => setPreviewUrl(dataUrl)}
                disabled={saving}
                uploadFn={uploadCustomizationLogo}
                deleteFn={deleteCustomizationLogo}
              />
            </CardContent>
          </Card>

          {}
          <Card className="shadow-none border">
            <CardHeader className="py-2 px-4">
              <CardTitle className="text-xs">{t('customization.preview', 'Pré-visualização')}</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-0">
              <div className="bg-muted/20 border border-border rounded-md p-2">
                <div className="flex justify-between items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-semibold truncate">
                      ATA N.º 1 | Projeto
                    </h3>
                    <p className="text-[10px] text-muted-foreground">
                      05/11/2025
                    </p>
                  </div>

                  {}
                  {previewUrl ? (
                    <div className="flex-shrink-0">
                      <img
                        src={previewUrl}
                        alt="Logo Preview"
                        className="h-8 w-auto max-w-[100px] object-contain"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-8 w-16 border border-dashed border-border rounded bg-background">
                      <span className="text-[9px] text-muted-foreground">
                        Logo
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="gap-2 flex-shrink-0 mt-4">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={saving}
            size="sm"
          >
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary/90"
            size="sm"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-primary-foreground mr-1.5"></div>
                {t('common.saving', 'Salvando...')}
              </>
            ) : (
              t('common.save', 'Salvar')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
